import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import './InteractiveWorksheet.css';
import { LessonSectionHeading } from './lesson-text/LessonTextContent';

const MATCH_COLORS = [
  'bg-blue-500 border-blue-500',
  'bg-amber-500 border-amber-500',
  'bg-purple-500 border-purple-500',
  'bg-pink-500 border-pink-500',
  'bg-teal-500 border-teal-500',
  'bg-orange-500 border-orange-500',
];

const InteractiveWorksheet = ({ data, onScoreSubmit, theme = 'dark', readingDesign = false }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  // Match state: { [qKey]: { selectedRight: idx|null, pairs: {rightIdx: leftIdx} } }
  const [matchState, setMatchState] = useState({});

  // Shuffled left columns (stable per question)
  const shuffledLefts = useMemo(() => {
    if (!data?.sections) return {};
    const result = {};
    data.sections.forEach((section, sIdx) => {
      section.questions.forEach((q, qIdx) => {
        if (q.type === 'match' && q.pairs) {
          const key = `${sIdx}-${qIdx}`;
          const indices = q.pairs.map((_, i) => i);
          for (let i = indices.length - 1; i > 0; i--) {
            const j = (sIdx * 7 + qIdx * 13 + i * 3) % (i + 1);
            [indices[i], indices[j]] = [indices[j], indices[i]];
          }
          result[key] = indices;
        }
      });
    });
    return result;
  }, [data]);

  if (!data?.sections) return null;

  const handleAnswer = (sIdx, qIdx, value) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [`${sIdx}-${qIdx}`]: value }));
  };

  const handleMatchClick = (qKey, side, idx) => {
    if (submitted) return;
    setMatchState(prev => {
      const current = prev[qKey] || { selectedRight: null, pairs: {} };
      
      if (side === 'right') {
        // If already matched, unmatch it
        if (current.pairs[idx] !== undefined) {
          const newPairs = { ...current.pairs };
          delete newPairs[idx];
          return { ...prev, [qKey]: { selectedRight: null, pairs: newPairs } };
        }
        // Select this right item
        return { ...prev, [qKey]: { ...current, selectedRight: idx } };
      }
      
      if (side === 'left') {
        // If no right selected, ignore
        if (current.selectedRight === null) return prev;
        // If this left is already matched to something else, unmatch that first
        const newPairs = { ...current.pairs };
        Object.keys(newPairs).forEach(k => {
          if (newPairs[k] === idx) delete newPairs[k];
        });
        // Create the pair
        newPairs[current.selectedRight] = idx;
        return { ...prev, [qKey]: { selectedRight: null, pairs: newPairs } };
      }
      return prev;
    });
  };

  const getMatchPairNum = (qKey, side, idx) => {
    const state = matchState[qKey];
    if (!state?.pairs) return null;
    if (side === 'right') {
      return state.pairs[idx] !== undefined ? Object.keys(state.pairs).sort().indexOf(String(idx)) : null;
    }
    // left side
    const rightKey = Object.keys(state.pairs).find(k => state.pairs[k] === idx);
    return rightKey !== undefined ? Object.keys(state.pairs).sort().indexOf(rightKey) : null;
  };

  const getScore = () => {
    let correct = 0, total = 0;
    data.sections.forEach((section, sIdx) => {
      section.questions.forEach((q, qIdx) => {
        const key = `${sIdx}-${qIdx}`;
        if (q.type === 'multiple_choice') {
          total++;
          if (answers[key] === q.correct_answer) correct++;
        } else if (q.type === 'fill_blank') {
          total++;
          const u = (answers[key] || '').trim().replace(/[\u064B-\u065F]/g, '');
          const c = q.correct_answer.trim().replace(/[\u064B-\u065F]/g, '');
          if (u === c) correct++;
        } else if (q.type === 'true_false') {
          total++;
          if (answers[key] === q.correct_answer) correct++;
        } else if (q.type === 'match' && q.pairs) {
          const state = matchState[key];
          q.pairs.forEach((_, pIdx) => {
            total++;
            if (state?.pairs?.[pIdx] === pIdx) correct++;
          });
        }
      });
    });
    return { correct, total };
  };

  const getWrongQuestions = () => {
    const wrong = [];
    data.sections.forEach((section, sIdx) => {
      section.questions.forEach((q, qIdx) => {
        const key = `${sIdx}-${qIdx}`;
        if (q.type === 'multiple_choice') {
          if (answers[key] !== q.correct_answer) {
            wrong.push({
              question: q.question,
              correct_answer: q.options[q.correct_answer],
              student_answer: answers[key] !== undefined ? q.options[answers[key]] : 'لم يجب'
            });
          }
        } else if (q.type === 'fill_blank') {
          const u = (answers[key] || '').trim().replace(/[\u064B-\u065F]/g, '');
          const c = q.correct_answer.trim().replace(/[\u064B-\u065F]/g, '');
          if (u !== c) {
            wrong.push({ question: q.question, correct_answer: q.correct_answer, student_answer: answers[key] || 'لم يجب' });
          }
        } else if (q.type === 'true_false') {
          if (answers[key] !== q.correct_answer) {
            wrong.push({ question: q.question, correct_answer: q.correct_answer ? 'صح' : 'خطأ', student_answer: answers[key] === true ? 'صح' : answers[key] === false ? 'خطأ' : 'لم يجب' });
          }
        } else if (q.type === 'match' && q.pairs) {
          const state = matchState[key];
          q.pairs.forEach((pair, pIdx) => {
            if (!(state?.pairs?.[pIdx] === pIdx)) {
              wrong.push({ question: `وصّل: ${pair.right}`, correct_answer: pair.left, student_answer: state?.pairs?.[pIdx] !== undefined ? q.pairs[state.pairs[pIdx]]?.left : 'لم يوصّل' });
            }
          });
        }
      });
    });
    return wrong;
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const s = getScore();
    const wrong = getWrongQuestions();
    if (onScoreSubmit) onScoreSubmit(s.correct, s.total, wrong);
  };
  const handleReset = () => { setAnswers({}); setSubmitted(false); setMatchState({}); };

  const isCorrect = (sIdx, qIdx, q) => {
    const key = `${sIdx}-${qIdx}`;
    if (q.type === 'multiple_choice') return answers[key] === q.correct_answer;
    if (q.type === 'fill_blank') {
      const a = (answers[key] || '').trim().replace(/[\u064B-\u065F]/g, '');
      return a === q.correct_answer.trim().replace(/[\u064B-\u065F]/g, '');
    }
    if (q.type === 'true_false') return answers[key] === q.correct_answer;
    return false;
  };

  const score = submitted ? getScore() : null;

  return (
    <div className={`interactive-worksheet space-y-6${theme === 'light' ? ' worksheet-light' : ''}${readingDesign ? ' lesson-exercise' : ''}`} data-testid="interactive-worksheet" data-theme={theme}>
      <h3 className="font-bold text-lg text-white text-center" data-testid="worksheet-title">{data.title}</h3>

      {submitted && score && (
        <div className={`text-center p-4 rounded-xl ${score.correct === score.total ? 'bg-green-500/20 border border-green-500/30' : score.correct >= score.total / 2 ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-red-500/20 border border-red-500/30'}`} data-testid="worksheet-score" aria-live="polite">
          <p className="text-2xl font-bold text-white" data-testid="worksheet-score-value">{score.correct} / {score.total}</p>
          <p className="text-white/70 text-sm mt-1" data-testid="worksheet-score-feedback">
            {score.correct === score.total ? 'ممتاز! إجابات كاملة' : score.correct >= score.total / 2 ? 'جيد! حاول تحسين إجاباتك' : 'حاول مرة أخرى'}
          </p>
        </div>
      )}

      {data.sections.map((section, sIdx) => (
        <div key={sIdx} className="space-y-3">
          {readingDesign ? <LessonSectionHeading number={sIdx + 1} tone={['rose', 'sky', 'sun', 'leaf'][sIdx % 4]} level="h4" testId={`worksheet-section-title-${sIdx}`}>{section.section_title}</LessonSectionHeading> : <h4 className="font-semibold text-[#4da6ff] text-base" data-testid={`worksheet-section-title-${sIdx}`}>{section.section_title}</h4>}

          {section.questions.map((q, qIdx) => {
            const key = `${sIdx}-${qIdx}`;
            const correct = submitted ? isCorrect(sIdx, qIdx, q) : null;

            return (
              <div key={qIdx} className="ws-question bg-white/5 rounded-xl p-4 space-y-3" data-testid={`worksheet-question-${sIdx}-${qIdx}`}>
                {q.type !== 'match' && (
                  <div className="flex items-start gap-2">
                    <p className="font-medium text-white/90 flex-1" data-testid={`worksheet-question-text-${sIdx}-${qIdx}`}>{q.question}</p>
                    {submitted && (
                      correct ? <CheckCircle className="w-5 h-5 text-green-400 shrink-0" /> : <XCircle className="w-5 h-5 text-red-400 shrink-0" />
                    )}
                  </div>
                )}

                {/* Multiple Choice */}
                {q.type === 'multiple_choice' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {q.options.map((opt, oIdx) => {
                      const selected = answers[key] === oIdx;
                      const isCorrectOpt = submitted && oIdx === q.correct_answer;
                      const isWrongSel = submitted && selected && oIdx !== q.correct_answer;
                      return (
                        <button key={oIdx} type="button" onClick={() => handleAnswer(sIdx, qIdx, oIdx)}
                          aria-pressed={selected} data-ws-state={isCorrectOpt ? 'correct' : isWrongSel ? 'incorrect' : selected ? 'selected' : 'idle'}
                          className={`ws-option p-3 rounded-lg border-2 text-right ${
                            isCorrectOpt ? 'border-green-500 bg-green-500/20 text-green-300' :
                            isWrongSel ? 'border-red-500 bg-red-500/20 text-red-300' :
                            selected ? 'border-[#4da6ff] bg-[#4da6ff]/30 text-white ring-1 ring-[#4da6ff]/50' :
                            'border-white/10 bg-white/5 text-white/80 hover:border-white/30'
                          }`}
                          data-testid={`ws-mc-${sIdx}-${qIdx}-${oIdx}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                              isCorrectOpt ? 'border-green-400 bg-green-400' :
                              isWrongSel ? 'border-red-400 bg-red-400' :
                              selected ? 'border-[#4da6ff] bg-[#4da6ff]' : 'border-white/30'
                            }`}>{(selected || isCorrectOpt) && <div className="w-1.5 h-1.5 rounded-full bg-white" />}</div>
                            <span className="ws-answer-label" data-testid={`ws-mc-label-${sIdx}-${qIdx}-${oIdx}`}>{opt}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Fill in the Blank */}
                {q.type === 'fill_blank' && (
                  <div>
                    <input type="text" dir="rtl" placeholder="اكتب إجابتك هنا..."
                      aria-label={q.question}
                      value={answers[key] || ''} onChange={(e) => handleAnswer(sIdx, qIdx, e.target.value)}
                      disabled={submitted}
                      className={`w-full p-3 rounded-lg border-2 bg-white/5 text-white placeholder-white/30 outline-none transition-all ${
                        submitted ? (correct ? 'border-green-500' : 'border-red-500') : 'border-white/10 focus:border-[#4da6ff]'
                      }`}
                      data-testid={`ws-fill-${sIdx}-${qIdx}`}
                    />
                    {submitted && !correct && (
                      <p className="text-green-400 text-sm mt-1" data-testid={`ws-correct-answer-${sIdx}-${qIdx}`}>الإجابة الصحيحة: {q.correct_answer}</p>
                    )}
                  </div>
                )}

                {/* True/False */}
                {q.type === 'true_false' && (
                  <div className="flex gap-3">
                    {[{ label: 'صح', value: true }, { label: 'خطأ', value: false }].map(({ label, value }) => {
                      const selected = answers[key] === value;
                      const isCorrectOpt = submitted && value === q.correct_answer;
                      const isWrongSel = submitted && selected && value !== q.correct_answer;
                      return (
                        <button key={label} type="button" onClick={() => handleAnswer(sIdx, qIdx, value)}
                          aria-pressed={selected} data-ws-state={isCorrectOpt ? 'correct' : isWrongSel ? 'incorrect' : selected ? 'selected' : 'idle'}
                          className={`ws-option flex-1 p-3 rounded-lg border-2 text-center font-semibold ${
                            isCorrectOpt ? 'border-green-500 bg-green-500/20 text-green-300' :
                            isWrongSel ? 'border-red-500 bg-red-500/20 text-red-300' :
                            selected ? 'border-[#4da6ff] bg-[#4da6ff]/30 text-white' :
                            'border-white/10 bg-white/5 text-white/80 hover:border-white/30'
                          }`}
                          data-testid={`ws-tf-${sIdx}-${qIdx}-${value}`}
                        >{label}</button>
                      );
                    })}
                  </div>
                )}

                {/* Match - Simplified with visual pairing */}
                {q.type === 'match' && q.pairs && (() => {
                  const mState = matchState[key] || { selectedRight: null, pairs: {} };
                  const leftOrder = shuffledLefts[key] || q.pairs.map((_, i) => i);

                  return (
                    <div>
                      <p className="font-medium text-white/90 mb-2" data-testid={`ws-match-title-${sIdx}-${qIdx}`}>وصّل بين العمود الأيمن والأيسر:</p>
                      <p className="text-white/40 text-xs mb-3" data-testid={`ws-match-help-${sIdx}-${qIdx}`}>اضغط عنصر من اليمين ثم عنصر من اليسار لتوصيلهما. اضغط عنصر موصّل لإلغائه.</p>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Right Column */}
                        <div className="space-y-2">
                          {q.pairs.map((pair, pIdx) => {
                            const pairNum = getMatchPairNum(key, 'right', pIdx);
                            const isSelected = mState.selectedRight === pIdx;
                            const isPaired = pairNum !== null;
                            const colorClass = isPaired ? MATCH_COLORS[pairNum % MATCH_COLORS.length] : '';
                            const isCorrectMatch = submitted && mState.pairs?.[pIdx] === pIdx;
                            const isWrongMatch = submitted && isPaired && mState.pairs?.[pIdx] !== pIdx;

                            return (
                              <button key={pIdx} type="button" onClick={() => handleMatchClick(key, 'right', pIdx)}
                                aria-pressed={isSelected || isPaired} data-ws-state={isCorrectMatch ? 'correct' : isWrongMatch ? 'incorrect' : isSelected ? 'active' : isPaired ? 'paired' : 'idle'}
                                className={`ws-option w-full p-3 rounded-lg border-2 text-right flex items-center justify-between gap-2 ${
                                  isCorrectMatch ? 'border-green-500 bg-green-500/20' :
                                  isWrongMatch ? 'border-red-500 bg-red-500/20' :
                                  isSelected ? 'border-amber-400 bg-amber-400/20 ring-2 ring-amber-400/50' :
                                  isPaired ? `${colorClass.split(' ')[1]} bg-opacity-20` :
                                  'border-white/10 bg-white/5 hover:border-white/30'
                                }`}
                                data-testid={`ws-match-r-${sIdx}-${qIdx}-${pIdx}`}
                              >
                                <span className="ws-answer-label text-white/90" data-testid={`ws-match-r-label-${sIdx}-${qIdx}-${pIdx}`}>{pair.right}</span>
                                {isPaired && (
                                  <span className={`ws-match-badge w-6 h-6 rounded-full ${colorClass.split(' ')[0]} text-white text-xs font-bold flex items-center justify-center shrink-0`} data-testid={`ws-match-r-pair-${sIdx}-${qIdx}-${pIdx}`}>
                                    {pairNum + 1}
                                  </span>
                                )}
                                {isSelected && !isPaired && (
                                  <span className="ws-match-pending w-6 h-6 rounded-full bg-amber-400 animate-pulse text-white text-xs font-bold flex items-center justify-center shrink-0" data-testid={`ws-match-pending-${sIdx}-${qIdx}-${pIdx}`}>?</span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {/* Left Column (shuffled) */}
                        <div className="space-y-2">
                          {leftOrder.map((originalIdx, displayIdx) => {
                            const pair = q.pairs[originalIdx];
                            const pairNum = getMatchPairNum(key, 'left', originalIdx);
                            const isPaired = pairNum !== null;
                            const colorClass = isPaired ? MATCH_COLORS[pairNum % MATCH_COLORS.length] : '';
                            const waiting = mState.selectedRight !== null && !isPaired;

                            return (
                              <button key={displayIdx} type="button" onClick={() => handleMatchClick(key, 'left', originalIdx)}
                                aria-pressed={isPaired} data-ws-state={isPaired ? 'paired' : 'idle'}
                                className={`ws-option w-full p-3 rounded-lg border-2 text-right flex items-center justify-between gap-2 ${
                                  isPaired ? `${colorClass.split(' ')[1]} bg-opacity-20` :
                                  waiting ? 'border-white/20 bg-white/5 hover:border-amber-400/50 hover:bg-amber-400/10' :
                                  'border-white/10 bg-white/5 hover:border-white/30'
                                }`}
                                data-testid={`ws-match-l-${sIdx}-${qIdx}-${displayIdx}`}
                              >
                                <span className="ws-answer-label text-white/90" data-testid={`ws-match-l-label-${sIdx}-${qIdx}-${displayIdx}`}>{pair.left}</span>
                                {isPaired && (
                                  <span className={`ws-match-badge w-6 h-6 rounded-full ${colorClass.split(' ')[0]} text-white text-xs font-bold flex items-center justify-center shrink-0`} data-testid={`ws-match-l-pair-${sIdx}-${qIdx}-${displayIdx}`}>
                                    {pairNum + 1}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {submitted && (
                        <div className="mt-2 space-y-1">
                          {q.pairs.map((pair, pIdx) => {
                            const userMatch = mState.pairs?.[pIdx];
                            const isRight = userMatch === pIdx;
                            return (
                              <p key={pIdx} className={`text-xs ${isRight ? 'text-green-400' : 'text-red-400'}`} data-testid={`ws-match-feedback-${sIdx}-${qIdx}-${pIdx}`}>
                                {pair.right} ← {isRight ? pair.left : (userMatch !== undefined ? q.pairs[userMatch]?.left : '(لم يوصّل)')} {!isRight && `(الصحيح: ${pair.left})`}
                              </p>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      ))}

      <div className="flex gap-3 pt-2">
        {!submitted ? (
          <Button onClick={handleSubmit} className="flex-1 bg-[#4da6ff] hover:bg-[#3d96ef] text-white" data-testid="ws-submit-btn">
            تحقق من إجاباتي
          </Button>
        ) : (
          <Button onClick={handleReset} className="flex-1 bg-amber-500/80 hover:bg-amber-500 text-white" data-testid="ws-reset-btn">
            <RotateCcw className="w-4 h-4 me-2" />
            حاول مرة أخرى
          </Button>
        )}
      </div>
    </div>
  );
};

export default InteractiveWorksheet;
